import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useParams } from "react-router";
import type { ClientSummary, TaskSummary } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Spinner } from "@/components/ui/Spinner";
import { Tabs } from "@/components/ui/Tabs";
import { useClientFromList } from "@/hooks/use-clients";
import { useTaskList, useTrashTask } from "@/hooks/use-tasks";
import { refusalDetail } from "@/services/api-client";
import { ClientLogo, UnassignedMark } from "./ClientLogo";
import { BackToClients, ClientsPageFrame } from "./ClientsLayout";
import { DraftRow } from "./DraftRow";
import { resolveDraftTitle } from "../layout/draft-title";
import { EditDraftDialog } from "./EditDraftDialog";
import { RecentlyDeleted } from "./RecentlyDeleted";
import { NewDraftDialog } from "./NewDraftDialog";
import type { Crumb } from "../layout/Header";
import { StagePage } from "../layout/StagePage";
import { CLIENTS_PATH, UNASSIGNED_TITLE, clientSwitcher } from "./paths";
import { isCompleted } from "./draft-standing";
import { PageNotFound } from "../layout/PageNotFound";

const CLIENT_LEDE =
  "Open a draft to pick up where you left off, or start a new draft for this client.";
const UNASSIGNED_LEDE =
  "Drafts made before they could be filed under a client. Open one to pick up where you left off.";

/** As many drafts as one page of the list serves; a client holding more is not a pilot's. */
const PAGE = 100;

function DraftList({
  drafts,
  empty,
  onEdit,
  onDelete,
}: {
  drafts: readonly TaskSummary[];
  empty: string;
  onEdit: (draft: TaskSummary) => void;
  onDelete: (draft: TaskSummary) => void;
}) {
  if (drafts.length === 0) {
    return (
      <p className="px-0.5 pt-0.5 text-detail text-subtle-foreground">
        {empty}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2.5">
      {drafts.map((draft) => (
        <DraftRow
          key={draft.task_id}
          draft={draft}
          onEdit={() => {
            onEdit(draft);
          }}
          onDelete={() => {
            onDelete(draft);
          }}
        />
      ))}
    </ul>
  );
}

/**
 * Moves a draft to Recently deleted once the attorney has confirmed it, and
 * says so. A refusal, work still running on the draft, is told in the server's
 * words.
 */
function useSetAside() {
  const trash = useTrashTask();
  // Each call awaits its own answer: two deletes in flight share one mutation, and a
  // per-call callback given to mutate runs for the latest call alone.
  return (draft: TaskSummary) => {
    trash.mutateAsync(draft.task_id).then(
      () => {
        toast.success("Draft deleted.");
      },
      (error: unknown) => {
        toast.error(refusalDetail(error, "The draft could not be deleted."));
      },
    );
  };
}

/** The question before a draft is set aside, naming it, where it goes from, and what goes with it. */
function DeleteDraftDialog({
  draft,
  owner,
  onCancel,
  onConfirm,
}: {
  draft: TaskSummary | null;
  owner: string;
  onCancel: () => void;
  onConfirm: (draft: TaskSummary) => void;
}) {
  return (
    <ConfirmDialog
      open={draft !== null}
      destructive
      warn
      title="Delete this draft?"
      description={
        draft === null
          ? ""
          : `"${resolveDraftTitle(draft)}" will be removed from ${owner}.`
      }
      note={
        <>
          Everything in the draft goes with it: uploaded documents, elements,
          positions, claims and the specification. The draft moves to{" "}
          <b className="font-bold">Recently deleted</b>, where it can be
          restored.
        </>
      }
      actionLabel="Delete Draft"
      onCancel={onCancel}
      onConfirm={() => {
        if (draft !== null) onConfirm(draft);
      }}
    />
  );
}

type List = "progress" | "completed" | "deleted";

/** The tabs over the drafts, each with its count; Recently deleted only while it holds one. */
function listTabs(
  inProgress: readonly TaskSummary[],
  completed: readonly TaskSummary[],
  setAside: readonly TaskSummary[],
): { id: List; label: string; count: number }[] {
  const tabs: { id: List; label: string; count: number }[] = [
    { id: "progress", label: "In progress", count: inProgress.length },
    { id: "completed", label: "Completed", count: completed.length },
  ];
  return setAside.length === 0
    ? tabs
    : [
        ...tabs,
        { id: "deleted", label: "Recently deleted", count: setAside.length },
      ];
}

/**
 * The drafts, as tabs with their counts: completed, everything else in
 * progress, and Recently deleted while it holds a draft. A tab that empties
 * while open leaves the attorney on In progress.
 */
function HubDrafts({
  drafts,
  setAside,
  loading,
  unassigned,
  owner,
}: {
  drafts: readonly TaskSummary[];
  setAside: readonly TaskSummary[];
  loading: boolean;
  unassigned: boolean;
  owner: string;
}) {
  const [open, setOpen] = useState<List>("progress");
  const [editing, setEditing] = useState<TaskSummary | null>(null);
  const [deleting, setDeleting] = useState<TaskSummary | null>(null);
  const setDraftAside = useSetAside();
  if (loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner size="sm" />
        Loading drafts…
      </p>
    );
  }
  const inProgress = drafts.filter((d) => !isCompleted(d));
  const completed = drafts.filter(isCompleted);
  const tabs = listTabs(inProgress, completed, setAside);
  const shown = tabs.some((tab) => tab.id === open) ? open : "progress";
  return (
    <>
      <Tabs label="Drafts" tabs={tabs} selected={shown} onSelect={setOpen}>
        {shown === "deleted" ? (
          <RecentlyDeleted drafts={setAside} />
        ) : (
          <DraftList
            drafts={shown === "progress" ? inProgress : completed}
            empty={
              shown === "completed"
                ? "No completed drafts yet."
                : unassigned
                  ? "No drafts in progress without a client."
                  : "No drafts in progress for this client."
            }
            onEdit={setEditing}
            onDelete={setDeleting}
          />
        )}
      </Tabs>
      <DeleteDraftDialog
        draft={deleting}
        owner={owner}
        onCancel={() => {
          setDeleting(null);
        }}
        onConfirm={(draft) => {
          setDeleting(null);
          setDraftAside(draft);
        }}
      />
      {editing !== null && (
        <EditDraftDialog
          key={editing.task_id}
          draft={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

/** The hub's one act: start a draft under this client, through the modal that asks for its details. */
function StartDraft({ clientId }: { clientId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-[26px]">
      <Button
        size="lead"
        onClick={() => {
          setOpen(true);
        }}
      >
        <Plus aria-hidden />
        New Patent Draft
      </Button>
      <NewDraftDialog
        clientId={clientId}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      />
    </div>
  );
}

/** The hub's trail: the clients, then this one, which switches to another of the caller's. */
function hubCrumbs(
  title: string,
  client: ClientSummary | undefined,
  clients: readonly ClientSummary[] = [],
): Crumb[] {
  return [
    { label: "Your clients", to: CLIENTS_PATH },
    {
      label: title,
      switcher:
        client === undefined
          ? undefined
          : clientSwitcher(clients, client.client_id),
    },
  ];
}

/**
 * A client's drafts, in progress and completed, and the way to start another.
 *
 * A draft is started through the new draft modal, which asks for what the draft
 * is made with. Mounted once more at the drafts under no client, where there is
 * no client to make a draft under and so no act at all.
 */
export function ClientHubPage({
  unassigned = false,
}: {
  unassigned?: boolean;
}) {
  const { clientId } = useParams<{ clientId: string }>();
  const clients = useClientFromList(unassigned ? undefined : clientId);
  const { client } = clients;
  const scope = unassigned ? { without_client: true } : { client_id: clientId };
  const reading = { enabled: unassigned || client !== undefined };
  const drafts = useTaskList({ ...scope, limit: PAGE }, reading);
  const setAside = useTaskList(
    { ...scope, deleted: "only", limit: PAGE },
    reading,
  );

  // A client that is not the caller's, or no longer exists, is absent from their
  // list, so the page says so, with the way back to it, rather than drawing nothing.
  if (!unassigned && clients.isSuccess && client === undefined) {
    return (
      <PageNotFound
        title="This client could not be found"
        reason="It may have been deleted, or it is not one of yours."
        problem="open a client"
        crumbs={[{ label: "Your clients", to: CLIENTS_PATH }]}
      />
    );
  }
  const title = unassigned ? UNASSIGNED_TITLE : (client?.name ?? "");
  const logo = unassigned ? (
    <UnassignedMark />
  ) : (
    <ClientLogo
      name={title}
      logoUrl={client?.logo_url ?? null}
      onBroken={() => {
        void clients.refetch({ cancelRefetch: false });
      }}
    />
  );

  return (
    <ClientsPageFrame crumbs={hubCrumbs(title, client, clients.data?.clients)}>
      {!unassigned && client === undefined ? (
        <div className="pt-10">
          <BackToClients />
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner size="sm" />
            Loading the client…
          </p>
        </div>
      ) : (
        <StagePage
          guide="home"
          testId="client-hub"
          back={<BackToClients />}
          mark={logo}
          title={title}
          lede={unassigned ? UNASSIGNED_LEDE : CLIENT_LEDE}
        >
          <div className="mt-8">
            {client && <StartDraft clientId={client.client_id} />}
            <HubDrafts
              drafts={drafts.data?.items ?? []}
              setAside={setAside.data?.items ?? []}
              loading={drafts.isLoading}
              unassigned={unassigned}
              owner={title}
            />
          </div>
        </StagePage>
      )}
    </ClientsPageFrame>
  );
}
